import React, { createContext, useContext, useState, useCallback, useEffect, Component } from "react";
import { PermissionsProvider } from "./context/PermissionsContext";

// ── Error Boundary: catches render crashes and shows a legible error instead of blank screen ──
class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error("[CRM ErrorBoundary]", err, info.componentStack); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontFamily: "'Inter',sans-serif", background: "#f8fafc", padding: 40 }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", marginBottom: 8 }}>Something went wrong</div>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 4, textAlign: "center", maxWidth: 480 }}>{this.state.error.message}</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 24, textAlign: "center", maxWidth: 480, fontFamily: "monospace" }}>{this.state.error.stack?.split("\n")[1]}</div>
          <button onClick={() => window.location.reload()} style={{ background: "#E31E24", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Reload page</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import useIdleTimer from "./hooks/useIdleTimer";
import SessionLockOverlay from "./components/SessionLockOverlay";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import EmployeeDashboard  from "./pages/employee/Dashboard";
import ManagerDashboard   from "./pages/manager/Dashboard";
import AdminDashboard     from "./pages/admin/Dashboard";
import ExecutiveDashboard from "./pages/executive/Dashboard";
import DailyProductivity  from "./pages/DailyProductivity";
import PwaStatus from "./components/PwaStatus";
import LocationTracker from "./components/LocationTracker";
import EmployeeExperience from "./components/EmployeeExperience";
import CelebrationHub from "./components/CelebrationHub";
import InteractionPolish from "./components/InteractionPolish";
import DeviceNotifications from "./components/DeviceNotifications";
import NotificationBell from "./components/NotificationBell";
import CompanyChatWidget from "./components/CompanyChatWidget";
import { useFcmPush, unregisterFcmToken } from "./utils/useFcmPush";

const UNIFIED_LOGIN_URL = process.env.REACT_APP_HRMS_LOGIN_URL || "http://localhost:5175/login";
const defaultSsoUrl = `${window.location.protocol}//${window.location.hostname}:7000`;
const SSO_API_URL = process.env.REACT_APP_SSO_URL || defaultSsoUrl;

//  Auth Context
const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("crm_token"));
  const [user,  setUser]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("crm_user")); } catch { return null; }
  });

  const login = (t, u) => {
    localStorage.setItem("crm_token", t);
    localStorage.setItem("crm_user",  JSON.stringify(u));
    setToken(t); setUser(u);
  };
  const logout = async () => {
    const t = localStorage.getItem("crm_token");
    if (t) {
      // Unregister FCM token on logout
      await unregisterFcmToken(t);
    }
    const keepChat = localStorage.getItem("company_chat_keep_signed_in") === "1";
    const chatToken = localStorage.getItem("company_chat_token");
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    if (keepChat && chatToken) localStorage.setItem("company_chat_token", chatToken);
    if (!keepChat) localStorage.removeItem("company_chat_token");
    setToken(null); setUser(null);
    window.location.href = UNIFIED_LOGIN_URL;
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isLoggedIn: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route. Browser permissions are origin-specific, so CRM lets its
// official-hours dashboard tracker and visit/photo features request only the
// access they need instead of blocking the whole app for three permissions.
function Protected({ children, roles }) {
  const { isLoggedIn, user } = useAuth();

  if (!isLoggedIn) return <UnifiedLoginRedirect />;

  // Resolve effective CRM role — superadmin always bypasses
  const effectiveCrmRole = user?.role === "superadmin"
    ? "superadmin"
    : (user?.app_role_overrides?.crm || user?.role);

  if (roles && !roles.includes(effectiveCrmRole)) {
    // Redirect to appropriate view for their effective CRM role
    if (effectiveCrmRole === "employee") return <Navigate to="/dashboard" replace />;
    if (effectiveCrmRole === "manager" || effectiveCrmRole === "team_leader") return <Navigate to="/manager" replace />;
    return <UnifiedLoginRedirect />;
  }

  return children;
}

//  Role-based home redirect (respects per-app CRM role overrides)
function HomeRedirect() {
  const { user, isLoggedIn } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  // Superadmin always goes to admin. For everyone else, check CRM override.
  const role = user?.role === "superadmin"
    ? "superadmin"
    : (user?.app_role_overrides?.crm || user?.role);
  if (role === "executive")                         return <Navigate to="/executive" replace />;
  // superadmin/manager/team_leader always start on the Self dashboard
  // (they can switch to My Team / Admin via the context pills)
  if (role === "superadmin")                        return <Navigate to="/crm-self"  replace />;
  if (role === "manager" || role === "team_leader") return <Navigate to="/crm-self"  replace />;
  return <Navigate to="/dashboard" replace />;
}

function SsoLanding() {
  const { login } = useAuth();
  const location = useLocation();
  const didExchange = React.useRef(false);

  React.useEffect(() => {
    // React 18 Strict Mode double-invokes effects in development. SSO codes are single-use:
    // the second concurrent exchange request would always get 401 and redirect to HRMS.
    // This ref guard ensures only the first invocation proceeds.
    if (didExchange.current) return;
    didExchange.current = true;
    const params = new URLSearchParams(location.search);
    const ssoCode = params.get("sso_code");
    const ssoToken = params.get("sso_token");     // from SSO service (:7000)
    const legacyToken = params.get("token");       // from HRMS AppLauncher
    const legacyUser  = params.get("user");        // from HRMS AppLauncher

    async function completeSignIn() {
      let loggedInUser = null;
      const hrmsPassthrough = params.get("hrms_token");
      if (ssoCode) {
        const exchangeCode = async (code) => {
          const response = await fetch(`${SSO_API_URL.replace(/\/$/, "")}/sso/exchange`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.error || "SSO exchange failed");
          return payload;
        };

        // A PWA reload or duplicate browser navigation can consume the
        // one-time code before this page exchanges it. A trusted device may
        // securely request one fresh code, exactly as the Expense app does.
        let data;
        try {
          data = await exchangeCode(ssoCode);
        } catch (firstError) {
          const recovery = await fetch(`${SSO_API_URL.replace(/\/$/, "")}/sso/trusted/code`, {
            method: "POST",
            credentials: "include",
          });
          const recoveryData = await recovery.json();
          if (!recovery.ok) throw firstError;
          data = await exchangeCode(recoveryData.sso_code);
        }
        loggedInUser = data.user;
        login(data.token, data.user);
        localStorage.setItem("company_chat_token", data.token);
        localStorage.setItem("hrms_token", data.token);
      } else if (ssoToken && process.env.NODE_ENV !== "production") {
        const base64 = ssoToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        loggedInUser = JSON.parse(atob(base64));
        login(ssoToken, loggedInUser);
        localStorage.setItem("company_chat_token", ssoToken);
        localStorage.setItem("hrms_token", hrmsPassthrough || ssoToken);
      } else if (legacyToken && legacyUser) {
        // HRMS AppLauncher flow — user object already provided as JSON in URL
        loggedInUser = JSON.parse(decodeURIComponent(legacyUser));
        login(legacyToken, loggedInUser);
        localStorage.setItem("company_chat_token", legacyToken);
        localStorage.setItem("hrms_token", hrmsPassthrough || legacyToken);
      } else {
        window.location.replace(UNIFIED_LOGIN_URL);
        return;
      }
      // Permission decisions are device/browser specific. Do not bypass the CRM
      // permission gate after SSO; it will ask once on each new device.
      // Clear persisted context so every role (incl. superadmin) always lands
      // on the "self" pill after login/app-switch.
      const email = loggedInUser?.email ?? "";
      localStorage.removeItem(`role_ctx_crm_${email}`);
      window.location.replace("/");
    }

    completeSignIn().catch(() => {
      window.location.replace(UNIFIED_LOGIN_URL);
    });
  }, [location.search, login]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "Inter, sans-serif", color: "#1e293b" }}>
      Opening Sales CRM...
    </div>
  );
}

function UnifiedLoginRedirect() {
  React.useEffect(() => {
    window.location.replace(UNIFIED_LOGIN_URL);
  }, []);
  return null;
}

// ── Screenshot / Screen-recording deterrent ─────────────────────────────
// Blurs the page briefly when the tab loses focus (deters casual recorders).
// Intercepts Ctrl/Cmd+P to block print-to-PDF.

// ── Session idle timeout ─────────────────────────────────────────
function SessionGuard({ children }) {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(false);

  const handleIdle   = useCallback(() => { if (user) setLocked(true);  }, [user]);
  const handleLogout = useCallback(() => { setLocked(false); logout(); }, [logout]);

  const { reset } = useIdleTimer({
    onIdle:   handleIdle,
    onLogout: handleLogout,
    idleMs:   15 * 60 * 1000,
    logoutMs: 30 * 60 * 1000,
    enabled:  !!user,
  });

  const onUnlock = useCallback(() => { setLocked(false); reset(); }, [reset]);

  return (
    <>
      {children}
      {locked && user && (
        <SessionLockOverlay user={user} onUnlock={onUnlock} onLogout={handleLogout} />
      )}
    </>
  );
}

function AppInner() {
  return null;
}

// Reads the live user from AuthContext and provides module-level CRM permissions
function PermissionsInit({ children }) {
  const { user } = useAuth();
  return <PermissionsProvider user={user}>{children}</PermissionsProvider>;
}


// FCM initializer — registers push token once user is logged in
function FcmInit() {
  const { user, token } = useAuth();
  useFcmPush(user, token);
  return null;
}

function GlobalBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname === "/") return null;
  const goBack = () => {
    if ((window.history.state?.idx || 0) > 0) navigate(-1);
    else navigate("/", { replace: true });
  };
  return <button className="crm-global-back" onClick={goBack} aria-label="Go to previous screen">← Back</button>;
}


//  App
function CelebrationInit() { const { user } = useAuth(); return user ? <><CelebrationHub company={user.company} /><InteractionPolish company={user.company} /></> : null; }
function LocationTrackingInit() {
  const { user } = useAuth();
  const trackableRoles = ["employee", "team_leader", "manager"];
  return user && trackableRoles.includes(user.role) ? <LocationTracker user={user} /> : null;
}
export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <PermissionsInit>
      <SessionGuard>
      <AppInner />
      <DeviceNotifications />
      <Toaster position="top-right" toastOptions={{
        style: { background: "#1e1b4b", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" },
        success: { iconTheme: { primary: "#10b981", secondary: "#fff" } },
        error:   { iconTheme: { primary: "#ef4444", secondary: "#fff" } },
      }} />
      <BrowserRouter>
        <PwaStatus />
        <LocationTrackingInit />
        <EmployeeExperience />
        <CelebrationInit />
        <FcmInit />
        <GlobalBackButton />
        <CompanyChatWidget />
        <NotificationBell />
        <Routes>
          <Route path="/sso"       element={<SsoLanding />} />
          <Route path="/login"     element={<UnifiedLoginRedirect />} />
          <Route path="/"          element={<HomeRedirect />} />
          <Route path="/dashboard" element={
            <Protected roles={["employee"]}>
              <EmployeeDashboard />
            </Protected>
          } />
          <Route path="/manager" element={
            <Protected roles={["manager", "team_leader", "superadmin"]}>
              <ManagerDashboard />
            </Protected>
          } />
          <Route path="/admin" element={
            <Protected roles={["superadmin"]}>
              <AdminDashboard />
            </Protected>
          } />
          <Route path="/executive" element={
            <Protected roles={["executive"]}>
              <ExecutiveDashboard />
            </Protected>
          } />
          <Route path="/productivity" element={
            <Protected>
              <DailyProductivity />
            </Protected>
          } />
          {/* /crm-self — self-service view for admin/manager/team_leader (context pill: Self) */}
          <Route path="/crm-self" element={
            <Protected>
              <EmployeeDashboard />
            </Protected>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </SessionGuard>
      </PermissionsInit>
    </AuthProvider>
    </ErrorBoundary>
  );
}
